/**
 * DZ Shopper — page publique dédiée (/dz-shopper).
 * Fusionne l'ancien placeholder "coming soon" avec le formulaire de demande produit :
 * le client peut demander un article introuvable, suggérer une référence, ou demander
 * une alternative disponible — pour un ou plusieurs produits en une seule demande.
 * Catégories/marques viennent de settings.request_categories / request_brands (CMS + MCP).
 */
import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Upload, Loader2, CheckCircle2, X } from "lucide-react";
import { useLang, useSettings } from "../lib/providers";
import { goBack } from "../lib/navHelpers";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppFloater from "../components/WhatsAppFloater";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 3;
const MAX_PRODUCTS = 10;

const emptyProduct = () => ({
  id: (crypto.randomUUID ? crypto.randomUUID() : `p-${Date.now()}-${Math.random()}`),
  category: "",
  brand: "",
  name_or_ref: "",
  description: "",
  photos: [],
});

export default function DzShopperPage() {
  const { lang } = useLang();
  const { settings } = useSettings();
  const navigate = useNavigate();

  // Textes modifiables dans le CMS (réglages dz_shopper_*) ; le texte d'origine reste la valeur de repli
  const txt = (key, fr, ar) => (lang === "ar" ? settings?.[`dz_shopper_${key}_ar`] || ar : settings?.[`dz_shopper_${key}_fr`] || fr);

  const cats = (settings?.request_categories || []).filter((c) => c.enabled);
  const brs = (settings?.request_brands || []).filter((b) => b.enabled);

  const [products, setProducts] = useState([emptyProduct()]);
  const [requestType, setRequestType] = useState("next_arrival");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | success
  const [error, setError] = useState("");

  const updateProduct = (id, patch) => setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const addProduct = () => {
    if (products.length >= MAX_PRODUCTS) return;
    setProducts((prev) => [...prev, emptyProduct()]);
  };
  const removeProduct = (id) => setProducts((prev) => (prev.length > 1 ? prev.filter((p) => p.id !== id) : prev));

  const handlePhotoUpload = async (product, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || (product.photos || []).length >= MAX_PHOTOS) return;
    setUploadingId(product.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/product-requests/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateProduct(product.id, { photos: [...(product.photos || []), data.url] });
    } catch {
      toast.error(lang === "ar" ? "فشل تحميل الصورة" : "Échec de l'envoi de l'image");
    } finally {
      setUploadingId(null);
    }
  };

  const removePhoto = (product, url) => updateProduct(product.id, { photos: (product.photos || []).filter((u) => u !== url) });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!firstName.trim() || !phone.trim()) {
      setError(lang === "ar" ? "الاسم ورقم الهاتف مطلوبان" : "Prénom et contact WhatsApp/téléphone requis");
      return;
    }
    for (const p of products) {
      if (!p.name_or_ref.trim() && !p.description.trim()) {
        setError(lang === "ar" ? "أضف اسم المنتج أو وصفه لكل عنصر" : "Chaque produit doit avoir un nom/référence ou une description");
        return;
      }
    }
    setState("loading");
    try {
      await axios.post(`${API}/product-requests`, {
        products: products.map((p) => ({
          category: p.category,
          brand: p.brand,
          name_or_ref: p.name_or_ref.trim(),
          description: p.description.trim(),
          photos: p.photos,
        })),
        request_type: requestType,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        contact_phone: phone.trim(),
        contact_email: email.trim(),
      });
      setState("success");
    } catch (err) {
      setError(err?.response?.data?.detail || (lang === "ar" ? "خطأ، حاول مرة أخرى" : "Erreur, réessayez"));
      setState("idle");
    }
  };

  const intentOptions = [
    { value: "next_arrival", fr: "Je souhaite commander ce produit lors d'un prochain arrivage", ar: "أريد طلب هذا المنتج عند وصول الشحنة القادمة" },
    { value: "alternative", fr: "Je souhaite une proposition d'un produit équivalent disponible", ar: "أريد اقتراح منتج مماثل متوفر حاليا" },
    { value: "suggest_brand", fr: "Je souhaite suggérer cette marque/référence pour votre boutique", ar: "أريد اقتراح هذه العلامة/المرجع لمتجركم" },
  ];

  return (
    <div className="min-h-screen text-white overflow-x-hidden" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Header />
      <main className="pt-10 pb-24 px-4" data-testid="dz-shopper-page">
        <div className="max-w-2xl mx-auto">
          <button
            type="button"
            onClick={() => goBack(navigate)}
            className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-white/50 hover:text-neon transition-colors mb-6"
            data-testid="dz-shopper-back-to-shop"
          >
            ← {lang === "ar" ? "العودة إلى المتجر" : "Retour à la boutique"}
          </button>
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-neon/30 text-neon text-xs font-black uppercase tracking-widest mb-6">
              <span className="w-1.5 h-1.5 bg-neon rounded-full animate-pulse" />
              🎾 DZ Shopper
            </div>
            <h1 className="font-heading font-black uppercase tracking-tighter text-3xl sm:text-5xl mb-4">
              {txt("title", "Vous cherchez un article introuvable ?", "لا تجد المنتج الذي تبحث عنه؟")}
            </h1>
            <p className="text-white/60 text-sm sm:text-base max-w-lg mx-auto">
              {txt("intro", "Dites-nous ce que vous recherchez.", "أخبرنا بما تبحث عنه.")}
            </p>
          </div>

          {state === "success" ? (
            <div className="text-center py-16 border border-white/10" data-testid="dz-shopper-success">
              <CheckCircle2 className="w-12 h-12 text-neon mx-auto mb-4" />
              <p className="font-bold text-lg max-w-md mx-auto leading-relaxed">
                {lang === "ar"
                  ? "✅ شكرا على طلبك. سنقوم بدراسة بحثك ونعود إليك إذا لزم الأمر. يساعدنا طلبك أيضا في تحضير شحناتنا القادمة واختيار المنتجات الأكثر طلبا."
                  : "✅ Merci pour votre demande. Nous allons étudier votre recherche et reviendrons vers vous si nécessaire. Votre demande nous aide également à préparer nos prochains arrivages et à sélectionner les produits les plus demandés."}
              </p>
              <button
                onClick={() => navigate("/")}
                className="mt-8 inline-flex items-center gap-2 bg-neon text-black px-6 py-3 font-black uppercase tracking-widest text-sm hover:opacity-90"
                data-testid="dz-shopper-back-home"
              >
                {lang === "ar" ? "العودة إلى الموقع" : "Retour au site"}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6">
              {products.map((p, idx) => (
                <div key={p.id} className="border border-white/15 p-4 relative" data-testid={`dz-shopper-product-${idx}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] font-black uppercase tracking-widest text-neon">
                      {lang === "ar" ? `منتج ${idx + 1}` : `Produit ${idx + 1}`}
                    </span>
                    {products.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeProduct(p.id)}
                        className="text-white/40 hover:text-clay"
                        data-testid={`dz-shopper-remove-product-${idx}`}
                        aria-label="remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 mb-3">
                    <select
                      value={p.category}
                      onChange={(e) => updateProduct(p.id, { category: e.target.value })}
                      className="bg-black/60 border border-white/15 px-3 py-2 text-sm focus:border-neon outline-none"
                      data-testid={`dz-shopper-category-${idx}`}
                    >
                      <option value="">{lang === "ar" ? "الفئة (اختياري)" : "Catégorie (facultatif)"}</option>
                      {cats.map((c) => (
                        <option key={c.id} value={c.label_fr}>{c.label_fr}</option>
                      ))}
                    </select>
                    <select
                      value={p.brand}
                      onChange={(e) => updateProduct(p.id, { brand: e.target.value })}
                      className="bg-black/60 border border-white/15 px-3 py-2 text-sm focus:border-neon outline-none"
                      data-testid={`dz-shopper-brand-${idx}`}
                    >
                      <option value="">{lang === "ar" ? "العلامة التجارية (اختياري)" : "Marque (facultatif)"}</option>
                      {brs.map((b) => (
                        <option key={b.id} value={b.label_fr}>{b.label_fr}</option>
                      ))}
                    </select>
                  </div>

                  <input
                    value={p.name_or_ref}
                    onChange={(e) => updateProduct(p.id, { name_or_ref: e.target.value })}
                    placeholder={lang === "ar" ? "الاسم الدقيق أو المرجع (مثال: Wilson Blade 98 v9)" : "Nom exact ou référence (ex: Wilson Blade 98 v9)"}
                    className="w-full bg-black/60 border border-white/15 px-3 py-2 text-sm focus:border-neon outline-none mb-3"
                    data-testid={`dz-shopper-name-${idx}`}
                  />

                  <textarea
                    value={p.description}
                    onChange={(e) => updateProduct(p.id, { description: e.target.value })}
                    rows={2}
                    placeholder={lang === "ar" ? "وصف حر (لون، مقاس، طراز، رابط...)" : "Description libre (couleur, taille, modèle, lien...)"}
                    className="w-full bg-black/60 border border-white/15 px-3 py-2 text-sm focus:border-neon outline-none resize-none mb-3"
                    data-testid={`dz-shopper-description-${idx}`}
                  />

                  <div className="flex items-center gap-2 flex-wrap">
                    {(p.photos || []).map((url) => (
                      <div key={url} className="relative">
                        <img src={url} alt="" className="w-14 h-14 object-cover border border-white/15" />
                        <button
                          type="button"
                          onClick={() => removePhoto(p, url)}
                          className="absolute -top-2 -end-2 bg-black border border-white/20 rounded-full w-5 h-5 flex items-center justify-center"
                          data-testid={`dz-shopper-remove-photo-${idx}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {(p.photos || []).length < MAX_PHOTOS && (
                      <label
                        className="flex items-center gap-2 border border-white/15 border-dashed px-3 py-2 text-xs text-white/60 cursor-pointer hover:border-neon transition-colors"
                        data-testid={`dz-shopper-upload-label-${idx}`}
                      >
                        {uploadingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {lang === "ar" ? `صورة (${(p.photos || []).length}/${MAX_PHOTOS})` : `Photo (${(p.photos || []).length}/${MAX_PHOTOS})`}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handlePhotoUpload(p, e)}
                          className="hidden"
                          data-testid={`dz-shopper-upload-input-${idx}`}
                        />
                      </label>
                    )}
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addProduct}
                disabled={products.length >= MAX_PRODUCTS}
                className="w-full flex items-center justify-center gap-2 border border-dashed border-white/20 hover:border-neon text-white/70 hover:text-neon px-4 py-3 text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-40"
                data-testid="dz-shopper-add-product"
              >
                <Plus className="w-4 h-4" />
                {lang === "ar" ? "إضافة منتج آخر" : "Ajouter un produit recherché"}
              </button>

              <div className="border border-white/15 p-4">
                <p className="text-[11px] font-black uppercase tracking-widest text-white/60 mb-3">
                  {lang === "ar" ? "ما هي رغبتك؟" : "Votre intention"}
                </p>
                <RadioGroup value={requestType} onValueChange={setRequestType} data-testid="dz-shopper-intent-group">
                  {intentOptions.map((opt) => (
                    <label key={opt.value} className="flex items-start gap-3 text-sm cursor-pointer py-1">
                      <RadioGroupItem value={opt.value} data-testid={`dz-shopper-intent-${opt.value}`} className="mt-0.5 border-white/40" />
                      <span className="text-white/85">{lang === "ar" ? opt.ar : opt.fr}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="border border-white/15 p-4 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={lang === "ar" ? "اللقب" : "Nom"}
                    className="bg-black/60 border border-white/15 px-3 py-2 text-sm focus:border-neon outline-none"
                    data-testid="dz-shopper-last-name"
                  />
                  <input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    placeholder={lang === "ar" ? "الاسم *" : "Prénom *"}
                    className="bg-black/60 border border-white/15 px-3 py-2 text-sm focus:border-neon outline-none"
                    data-testid="dz-shopper-first-name"
                  />
                </div>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  placeholder={lang === "ar" ? "واتساب / الهاتف *" : "WhatsApp / Téléphone *"}
                  className="w-full bg-black/60 border border-white/15 px-3 py-2 text-sm focus:border-neon outline-none"
                  data-testid="dz-shopper-phone"
                />
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={lang === "ar" ? "البريد الإلكتروني (اختياري)" : "Email (facultatif)"}
                    className="w-full bg-black/60 border border-white/15 px-3 py-2 text-sm focus:border-neon outline-none"
                    data-testid="dz-shopper-email"
                  />
                  <p className="text-[11px] text-white/40 mt-1.5" data-testid="dz-shopper-email-disclaimer">
                    {txt("disclaimer", "Votre email sera utilisé pour vous recontacter concernant votre demande et vous informer de nos nouveautés si vous l'acceptez.", "سيتم استخدام بريدك الإلكتروني للتواصل معك بخصوص طلبك، ولإعلامك بمنتجاتنا الجديدة إذا وافقت على ذلك.")}
                  </p>
                </div>
              </div>

              {error && (
                <p className="text-sm text-clay" data-testid="dz-shopper-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={state === "loading" || uploadingId}
                className="w-full bg-neon text-black hover:opacity-90 px-6 py-4 font-black uppercase tracking-widest text-sm disabled:opacity-50"
                data-testid="dz-shopper-submit"
              >
                {state === "loading" ? (
                  <Loader2 className="w-4 h-4 animate-spin inline-block" />
                ) : (
                  txt("button", "Envoyer ma demande", "إرسال طلبي")
                )}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer onAdminClick={() => {}} />
      <WhatsAppFloater />
    </div>
  );
}
